import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { gamesApi, usersApi, leaguesApi } from '../services/api';
import vipIcon from '../images/vip_icon2.png';
import appLogo from '../images/american_pub_poker.jpg';
import './Kiosk.css';

function Kiosk({ user, onLogin, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialGameId = searchParams.get('gameId');

  // Game selection state
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [loading, setLoading] = useState(true);

  // Table / seating state
  const [tables, setTables] = useState([]);
  const [gameInfo, setGameInfo] = useState(null);
  const [activeTableIndex, setActiveTableIndex] = useState(0);

  // User identification modal
  const [showUserModal, setShowUserModal] = useState(false);
  const [pendingSeat, setPendingSeat] = useState(null); // { tableId, seatNumber }
  const [identifyMode, setIdentifyMode] = useState('name'); // 'name' or 'username'
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [username, setUsername] = useState('');
  const [identifiedUser, setIdentifiedUser] = useState(null);
  const [userError, setUserError] = useState('');
  const [userStatusMsg, setUserStatusMsg] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [showCreatePrompt, setShowCreatePrompt] = useState(false);
  const [showPhonePrompt, setShowPhonePrompt] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  // Waiting list
  const [waitingList, setWaitingList] = useState([]);
  const [showWaitingListView, setShowWaitingListView] = useState(false);

  // VIP swap modal
  const [showVipSwapModal, setShowVipSwapModal] = useState(false);
  const [vipSwapStep, setVipSwapStep] = useState('password'); // 'password' | 'selectSeat' | 'confirm'
  const [vipUser, setVipUser] = useState(null);
  const [vipPassword, setVipPassword] = useState('');
  const [vipSwapTarget, setVipSwapTarget] = useState(null);
  const [vipSwapError, setVipSwapError] = useState('');
  const [vipSwapLoading, setVipSwapLoading] = useState(false);

  // Admin login modal
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminError, setAdminError] = useState('');
  const [adminLoggingIn, setAdminLoggingIn] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);

  // Fetch today's games on mount
  // Request browser fullscreen on mount
  useEffect(() => {
    const requestFullscreen = () => {
      const el = document.documentElement;
      if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    };
    // Fullscreen requires user gesture, so try on first click if not already fullscreen
    if (!document.fullscreenElement) {
      const handleClick = () => {
        if (!document.fullscreenElement) requestFullscreen();
        document.removeEventListener('click', handleClick);
      };
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, []);

  // Trap browser back button — prevent navigating away from kiosk
  useEffect(() => {
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      setLoading(true);
      const res = await gamesApi.getAll(user?.league?.id);
      const gamesList = res.data?.games || res.games || [];
      // Filter to today's and future games only
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const upcoming = gamesList
        .filter(g => new Date(g.startTime) >= todayStart && g.status !== 'completed')
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
      setGames(upcoming);

      // Auto-select game if gameId is in URL
      if (initialGameId) {
        const match = upcoming.find(g => g.id === initialGameId);
        if (match) {
          setSelectedGame(match);
          setActiveTableIndex(0);
        }
      }
    } catch (err) {
      console.error('Error fetching games:', err);
      showToast('error', 'Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  const fetchSeating = useCallback(async (gameId) => {
    try {
      const [seatingRes, waitingRes] = await Promise.all([
        gamesApi.getSeating(gameId),
        gamesApi.getWaitingList(gameId).catch(() => ({ data: { waitingList: [] } })),
      ]);
      if (seatingRes.success) {
        setGameInfo(seatingRes.data.game);
        setTables(seatingRes.data.tables || []);
      }
      const wl = waitingRes.data?.waitingList || waitingRes.waitingList || [];
      setWaitingList(wl);
    } catch (err) {
      console.error('Error fetching seating:', err);
      showToast('error', 'Failed to load seating');
    }
  }, []);

  // Poll seating every 10 seconds when a game is selected
  useEffect(() => {
    if (!selectedGame) return;
    fetchSeating(selectedGame.id);
    const interval = setInterval(() => fetchSeating(selectedGame.id), 10000);
    return () => clearInterval(interval);
  }, [selectedGame, fetchSeating]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSelectGame = (game) => {
    setSelectedGame(game);
    setActiveTableIndex(0);
  };

  const handleBackToGames = () => {
    setSelectedGame(null);
    setTables([]);
    setGameInfo(null);
    fetchGames();
  };

  // --- Seat click opens user identification modal ---
  const handleSeatClick = (tableId, seatNumber) => {
    const table = tables.find(t => t.tableId === tableId);
    const seat = table?.seats?.find(s => s.seatNumber === seatNumber);

    // VIP swap seat selection mode — allow clicking empty or non-VIP occupied seats
    if (showVipSwapModal && vipSwapStep === 'selectSeat') {
      if (seat?.isOccupied) {
        if (seat.isVip) {
          setVipSwapError('Cannot swap with another VIP member');
          return;
        }
        // Target is a non-VIP occupied seat
        setVipSwapTarget({
          tableId,
          seatNumber,
          occupant: { playerId: seat.playerId, playerName: seat.playerName },
        });
      } else {
        // Target is an empty seat
        setVipSwapTarget({ tableId, seatNumber, occupant: null });
      }
      setVipSwapStep('confirm');
      setVipSwapError('');
      return;
    }

    // Normal mode: occupied seat clicked
    if (seat?.isOccupied) {
      // If VIP seat, initiate VIP swap flow
      if (seat.isVip) {
        setVipUser({
          playerId: seat.playerId,
          playerName: seat.playerName,
          tableId,
          seatNumber,
        });
        setVipPassword('');
        setVipSwapTarget(null);
        setVipSwapError('');
        setVipSwapStep('password');
        setShowVipSwapModal(true);
      }
      return;
    }

    // Normal empty seat reservation flow
    setPendingSeat({ tableId, seatNumber });
    setShowUserModal(true);
    setIdentifyMode('name');
    setFirstName('');
    setLastName('');
    setPhoneNumber('');
    setNewPassword('');
    setUsername('');
    setIdentifiedUser(null);
    setUserError('');
    setUserStatusMsg('');
    setShowCreatePrompt(false);
    setShowPhonePrompt(false);
  };

  // --- User identification: look up by first+last+phone or username/phone ---
  const handleIdentifyUser = async () => {
    setUserError('');
    setUserStatusMsg('');
    setLookingUp(true);

    try {
      if (identifyMode === 'username') {
        // Username mode — also accepts phone number
        const input = username.trim();
        if (!input) {
          setUserError('Please enter a username or phone number');
          setLookingUp(false);
          return;
        }

        // Check if input looks like a phone number (mostly digits)
        const digitsOnly = input.replace(/\D/g, '');
        const isPhone = digitsOnly.length >= 7 && digitsOnly.length <= 11;

        let foundUser = null;

        if (isPhone) {
          // Try phone lookup first
          try {
            const phoneRes = await usersApi.getUserByPhone(digitsOnly);
            if (phoneRes.success && phoneRes.data?.id) {
              foundUser = phoneRes.data;
            }
          } catch (e) { /* not found by phone, try username */ }
        }

        if (!foundUser) {
          // Try username lookup
          try {
            const usernameRes = await usersApi.getUserByUsername(input);
            if (usernameRes.success && usernameRes.data?.id) {
              foundUser = usernameRes.data;
            }
          } catch (e) { /* not found */ }
        }

        if (!foundUser) {
          setUserError('User not found. Try using your name instead.');
          setLookingUp(false);
          return;
        }
        setIdentifiedUser(foundUser);
      } else {
        // Name mode — require only first name and last name
        if (!firstName.trim() || !lastName.trim()) {
          setUserError('First name and last name are required');
          setLookingUp(false);
          return;
        }

        // Try to find existing user by first+last name
        const res = await usersApi.getAll();
        const users = res.data?.users || res.users || [];
        const match = users.find(
          u =>
            (u.firstName || u.first_name || '').toLowerCase() === firstName.trim().toLowerCase() &&
            (u.lastName || u.last_name || '').toLowerCase() === lastName.trim().toLowerCase()
        );

        if (match) {
          setIdentifiedUser(match);
        } else {
          // User not found — prompt to create profile
          setShowCreatePrompt(true);
        }
      }
    } catch (err) {
      console.error('Error identifying user:', err);
      setUserError(err.message || 'Failed to look up user');
      setUserStatusMsg('');
    } finally {
      setLookingUp(false);
    }
  };

  // --- Create profile (after user confirms) ---
  const handleCreateProfile = async () => {
    try {
      const digitsOnly = phoneNumber.replace(/\D/g, '');
      if (digitsOnly.length < 7 || digitsOnly.length > 11) {
        setUserError('Please enter a valid phone number');
        return;
      }

      setLookingUp(true);
      setUserError('');

      setUserStatusMsg('Creating profile...');
      const createRes = await usersApi.create({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: digitsOnly,
        password: newPassword || digitsOnly.slice(-4),
      });

      if (createRes.success !== false && createRes.data?.id) {
        setIdentifiedUser(createRes.data);
        setShowPhonePrompt(false);
        setShowCreatePrompt(false);
        setUserStatusMsg('');
      } else {
        setUserError(createRes.message || 'Failed to create profile');
        setUserStatusMsg('');
      }
    } catch (err) {
      console.error('Error creating profile:', err);
      setUserError(err.message || 'Failed to create profile');
      setUserStatusMsg('');
    } finally {
      setLookingUp(false);
    }
  };

  // --- Confirm seat reservation ---
  const handleConfirmSeat = async () => {
    if (!identifiedUser || !pendingSeat || !selectedGame) return;
    setLookingUp(true);
    try {
      // Use venue location as user location (kiosk is at the venue)
      const venue = gameInfo?.venue || selectedGame?.venue;
      const res = await gamesApi.reserveSeat(
        selectedGame.id,
        identifiedUser.id,
        venue?.latitude || 0,
        venue?.longitude || 0,
        pendingSeat.tableId,
        pendingSeat.seatNumber
      );
      if (res.success !== false) {
        showToast('success', `Seat ${pendingSeat.seatNumber} reserved for ${identifiedUser.firstName || identifiedUser.first_name}!`);
        setShowUserModal(false);
        fetchSeating(selectedGame.id);
      }
    } catch (err) {
      console.error('Error reserving seat:', err);
      setUserError(err.message || 'Failed to reserve seat');
    } finally {
      setLookingUp(false);
    }
  };

  // --- Waiting list ---
  const handleJoinWaitingList = () => {
    setPendingSeat(null); // signals waiting list instead of seat
    setShowUserModal(true);
    setIdentifyMode('name');
    setFirstName('');
    setLastName('');
    setPhoneNumber('');
    setNewPassword('');
    setUsername('');
    setIdentifiedUser(null);
    setUserError('');
    setUserStatusMsg('');
    setShowCreatePrompt(false);
    setShowPhonePrompt(false);
  };

  const handleConfirmWaitingList = async () => {
    if (!identifiedUser || !selectedGame) return;
    setLookingUp(true);
    try {
      const venue = gameInfo?.venue || selectedGame?.venue;
      const res = await gamesApi.joinWaitingList(
        selectedGame.id,
        identifiedUser.id,
        venue?.latitude || 0,
        venue?.longitude || 0
      );
      if (res.success !== false) {
        showToast('success', `${identifiedUser.firstName || identifiedUser.first_name} added to waiting list!`);
        setShowUserModal(false);
        fetchSeating(selectedGame.id);
      }
    } catch (err) {
      console.error('Error joining waiting list:', err);
      setUserError(err.message || 'Failed to join waiting list');
    } finally {
      setLookingUp(false);
    }
  };

  // --- VIP Swap ---
  const handleVipPasswordSubmit = async () => {
    if (!vipPassword.trim()) {
      setVipSwapError('Please enter your password');
      return;
    }
    setVipSwapLoading(true);
    setVipSwapError('');

    try {
      const res = await usersApi.verifyVipPassword(vipUser.playerId, vipPassword.trim());
      if (res.success !== false) {
        setVipSwapStep('selectSeat');
      }
    } catch (err) {
      setVipSwapError(err.message || 'Invalid password');
    } finally {
      setVipSwapLoading(false);
    }
  };

  const handleVipSwapConfirm = async () => {
    if (!vipUser || !vipSwapTarget || !selectedGame) return;
    setVipSwapLoading(true);
    setVipSwapError('');

    try {
      const res = await gamesApi.swapSeat(
        selectedGame.id,
        vipUser.playerId,
        vipPassword.trim(),
        vipSwapTarget.tableId,
        vipSwapTarget.seatNumber
      );

      if (res.success !== false) {
        const msg = vipSwapTarget.occupant
          ? `Swapped with ${vipSwapTarget.occupant.playerName}`
          : `Moved to Seat ${vipSwapTarget.seatNumber}`;
        showToast('success', msg);
        setShowVipSwapModal(false);
        fetchSeating(selectedGame.id);
      }
    } catch (err) {
      setVipSwapError(err.message || 'Failed to swap seats');
    } finally {
      setVipSwapLoading(false);
    }
  };

  // --- Admin login ---
  const handleAdminLoginClick = () => {
    setShowAdminLogin(true);
    setAdminUsername('');
    setAdminPassword('');
    setAdminError('');
  };

  const handleAdminLoginSubmit = async () => {
    if (!adminUsername.trim()) {
      setAdminError('Please enter a username');
      return;
    }
    setAdminLoggingIn(true);
    setAdminError('');

    try {
      const response = await usersApi.getUserByUsername(adminUsername.trim());

      if (response.success && response.data?.id) {
        const roles = response.data.roles || [];

        if (!roles.includes('leagueAdmin') && !roles.includes('siteAdmin')) {
          setAdminError('Access denied. League admin privileges required.');
          setAdminLoggingIn(false);
          return;
        }

        let league = null;
        try {
          const leaguesRes = await leaguesApi.getAll();
          const leagues = leaguesRes.data || leaguesRes.leagues || [];
          if (leagues.length > 0) league = leagues[0];
        } catch (e) { /* ignore */ }

        const userData = {
          id: response.data.id,
          username: response.data.username,
          firstName: response.data.firstName || '',
          lastName: response.data.lastName || '',
          email: response.data.email,
          roles,
          league,
        };

        onLogin(userData);
        navigate('/dashboard');
      } else {
        setAdminError('User not found. Please check your username.');
      }
    } catch (err) {
      console.error('Admin login error:', err);
      setAdminError(err.message || 'Login failed. Please try again.');
    } finally {
      setAdminLoggingIn(false);
    }
  };

  // --- Seat position calculation (same as poker-native-web) ---
  const getSeatPosition = (seatNumber, maxSeats) => {
    const totalSeats = maxSeats || 10;
    const seatIndex = seatNumber - 1;
    const totalSegments = totalSeats + 1;
    const angleStep = 360 / totalSegments;
    const startAngle = -90 + angleStep;
    const angleDeg = startAngle + (seatIndex * angleStep);
    const angleRad = (angleDeg * Math.PI) / 180;
    const radiusX = 38;
    const radiusY = 32;
    const x = 50 + radiusX * Math.cos(angleRad);
    const y = 50 + radiusY * Math.sin(angleRad);
    return { left: `${x}%`, top: `${y}%` };
  };

  const formatPlayerName = (fullName) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1][0]}.`;
  };

  const formatStartTime = (time) => {
    if (!time) return '';
    const date = new Date(time);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // --- Check if all tables are full ---
  const allTablesFull = tables.length > 0 && tables.every(t => t.seats.every(s => s.isOccupied));

  // ===========================
  // RENDER: Loading
  // ===========================
  if (loading && !selectedGame) {
    return (
      <div className="kiosk-fullscreen">
        <div className="kiosk-loading">
          <div className="kiosk-spinner">♠</div>
          <p>Loading games...</p>
        </div>
      </div>
    );
  }

  // ===========================
  // RENDER: Game Selection
  // ===========================
  if (!selectedGame) {
    return (
      <div className="kiosk-fullscreen">
        <div className="kiosk-topbar">
          <div className="kiosk-topbar-left">
            <img src={appLogo} alt="American Pub Poker" className="kiosk-topbar-logo" />
            <h2>American Pub Poker</h2>
            <span className="kiosk-game-badge">Kiosk Mode</span>
          </div>
          <button className="kiosk-admin-login-btn" onClick={handleAdminLoginClick}>
            Admin Login
          </button>
        </div>

        <div className="kiosk-game-select">
          <h2>Select a Game</h2>
          <p>Tap a game to view tables and choose your seat</p>

          {games.length === 0 ? (
            <p style={{ color: 'rgba(255,255,255,0.5)' }}>No upcoming games found.</p>
          ) : (
            <div className="kiosk-game-list">
              {games.map(game => (
                <div
                  key={game.id}
                  className={`kiosk-game-card ${game.isFull ? 'full' : ''}`}
                  onClick={() => handleSelectGame(game)}
                >
                  <h4>{game.venue?.name || 'Unknown Venue'}</h4>
                  <div className="game-card-info">
                    <span>{game.gameType} - {formatStartTime(game.startTime)}</span>
                    <span>{new Date(game.startTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                  </div>
                  <div className={`game-card-seats ${game.isFull ? 'full' : ''}`}>
                    {game.isFull
                      ? `Full (${game.occupiedSeats}/${game.maxPlaces}) - Waiting list`
                      : `${game.availableSeats} of ${game.maxPlaces} seats available`
                    }
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Admin Login Modal */}
        {showAdminLogin && (
          <div className="kiosk-user-modal-overlay" onClick={() => setShowAdminLogin(false)}>
            <div className="kiosk-user-modal" onClick={e => e.stopPropagation()}>
              <h3>Admin Login</h3>
              <p className="modal-subtitle">Enter admin credentials to access the dashboard</p>

              <div className="kiosk-form-group">
                <label>Username</label>
                <input
                  value={adminUsername}
                  onChange={e => setAdminUsername(e.target.value)}
                  placeholder="Enter admin username"
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && adminPassword && handleAdminLoginSubmit()}
                />
              </div>
              <div className="kiosk-form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={adminPassword}
                  onChange={e => setAdminPassword(e.target.value)}
                  placeholder="Enter password"
                  onKeyDown={e => e.key === 'Enter' && handleAdminLoginSubmit()}
                />
              </div>

              {adminError && <div className="kiosk-modal-error">{adminError}</div>}

              <div className="kiosk-modal-actions">
                <button className="kiosk-btn-secondary" onClick={() => setShowAdminLogin(false)}>
                  Cancel
                </button>
                <button className="kiosk-btn-primary" onClick={handleAdminLoginSubmit} disabled={adminLoggingIn}>
                  {adminLoggingIn ? 'Signing in...' : 'Sign In'}
                </button>
              </div>
            </div>
          </div>
        )}

        {toast && <div className={`kiosk-toast ${toast.type}`}>{toast.message}</div>}
      </div>
    );
  }

  // ===========================
  // RENDER: Game Table View
  // ===========================
  const activeTable = tables[activeTableIndex] || null;
  const totalSeats = activeTable?.maxPlayers || gameInfo?.maxPlaces || 9;
  const occupiedCount = activeTable ? activeTable.seats.filter(s => s.isOccupied).length : 0;
  const locationName = gameInfo?.venue?.name || selectedGame.venue?.name || 'Poker Game';
  const gameType = gameInfo?.gameType || selectedGame.gameType || 'Texas Hold\'em';
  const startTime = gameInfo?.startTime || selectedGame.startTime;

  return (
    <div className="kiosk-fullscreen">
      <div className="kiosk-topbar">
        <div className="kiosk-topbar-left">
          <img src={gameInfo?.venue?.logoUrl || appLogo} alt="American Pub Poker" className="kiosk-topbar-logo" />
          <h2>{gameInfo?.venue?.kioskMessage || 'American Pub Poker'}</h2>
          <span className="kiosk-topbar-divider">|</span>
          <span className="kiosk-topbar-venue">{locationName}</span>
          <span className="kiosk-game-badge">{gameType}</span>
          {(activeTable?.dealer?.name || gameInfo?.dealer?.name) && (
            <>
              <span className="kiosk-topbar-divider">|</span>
              <span className="kiosk-topbar-dealer">Dealer: {activeTable?.dealer?.name || gameInfo?.dealer?.name}</span>
            </>
          )}
        </div>
        <button className="kiosk-admin-login-btn" onClick={handleAdminLoginClick}>
          Admin Login
        </button>
      </div>

      <div className="kiosk-body">
        {/* Table tabs — show when more than 1 table OR when tables are full (to show waiting list tab) */}
        {(tables.length > 1 || allTablesFull) && (
          <div className="kiosk-table-tabs">
            {tables.map((table, idx) => {
              const tableFull = table.seats.every(s => s.isOccupied);
              return (
                <button
                  key={table.tableId}
                  className={`kiosk-table-tab ${!showWaitingListView && idx === activeTableIndex ? 'active' : ''} ${tableFull ? 'full' : ''}`}
                  onClick={() => { setShowWaitingListView(false); setActiveTableIndex(idx); }}
                >
                  Table {table.tableNumber}
                  {' '}({table.seats.filter(s => s.isOccupied).length}/{table.maxPlayers || totalSeats})
                </button>
              );
            })}
            {allTablesFull && (
              <button
                className={`kiosk-table-tab waiting-list-tab ${showWaitingListView ? 'active' : ''}`}
                onClick={() => setShowWaitingListView(true)}
              >
                Waiting List {waitingList.length > 0 ? `(${waitingList.length})` : ''}
              </button>
            )}
          </div>
        )}

        {showWaitingListView ? (
          /* Waiting List View */
          <div className="kiosk-waiting-list-view">
            <div className="kiosk-waiting-list-header">
              <h2>Waiting List</h2>
              <p>Join the list to be seated when a spot opens</p>
            </div>

            {waitingList.length > 0 ? (
              <div className="kiosk-waiting-list-users">
                {waitingList.map((entry, idx) => (
                  <div key={entry.id} className="kiosk-waiting-list-user">
                    <span className="waiting-position">{idx + 1}</span>
                    <span className="waiting-name">{formatPlayerName(entry.playerName)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="kiosk-waiting-list-empty">No one on the waiting list yet.</div>
            )}

            <button className="kiosk-waiting-btn" onClick={handleJoinWaitingList}>
              Join Waiting List
            </button>
          </div>
        ) : (
          /* Poker table */
          <>
            <div className="kiosk-table-area">
              <div className="kiosk-poker-table">
                <div className="kiosk-table-felt">
                  <div className="kiosk-table-center">
                    <div className="bar-name">{locationName}</div>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <span className="game-type-badge">{gameType}</span>
                    </div>
                    <div className="choose-seat-text">Choose Seat to Play</div>
                  </div>
                </div>

                {/* Dealer at 12 o'clock */}
                <div className="kiosk-dealer" style={{ left: '50%', top: '12%' }}>
                  <div className="dealer-avatar">🎩</div>
                  <div className="dealer-name">{activeTable?.dealer?.name || gameInfo?.dealer?.name || 'Dealer'}</div>
                </div>

                {/* Seats */}
                {activeTable && [...Array(totalSeats)].map((_, index) => {
                  const seatNumber = index + 1;
                  const seat = activeTable.seats.find(s => s.seatNumber === seatNumber);
                  const isOccupied = seat?.isOccupied || false;
                  const position = getSeatPosition(seatNumber, totalSeats);

                  // VIP swap mode CSS classes
                  const isVipSelectMode = showVipSwapModal && vipSwapStep === 'selectSeat';
                  const isVipCurrentSeat = isVipSelectMode && vipUser &&
                    activeTable.tableId === vipUser.tableId && seatNumber === vipUser.seatNumber;
                  const isVipBlockedSeat = isVipSelectMode && isOccupied && seat?.isVip && !isVipCurrentSeat;
                  const isVipTargetSeat = isVipSelectMode && !isVipCurrentSeat && !isVipBlockedSeat;

                  let seatClasses = `kiosk-seat ${isOccupied ? 'occupied' : 'empty'}`;
                  if (isVipCurrentSeat) seatClasses += ' vip-current';
                  if (isVipBlockedSeat) seatClasses += ' vip-blocked';
                  if (isVipTargetSeat) seatClasses += ' vip-target';

                  return (
                    <div
                      key={seatNumber}
                      className={seatClasses}
                      style={position}
                      onClick={() => handleSeatClick(activeTable.tableId, seatNumber)}
                    >
                      {isOccupied ? (
                        <>
                          {seat.isVip && (
                            <img src={vipIcon} alt="VIP" className="kiosk-vip-icon" />
                          )}
                          <div className="player-avatar">👤</div>
                          <div className="player-name">{formatPlayerName(seat.playerName)}</div>
                        </>
                      ) : (
                        <>
                          <div className="empty-seat-icon">+</div>
                          <div className="seat-label">Seat {seatNumber}</div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="kiosk-player-count">
                <span className="count-badge">{occupiedCount}/{totalSeats} Players</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* User Identification Modal */}
      {showUserModal && (
        <div className="kiosk-user-modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="kiosk-user-modal" onClick={e => e.stopPropagation()}>
            <h3>{pendingSeat ? `Reserve Seat ${pendingSeat.seatNumber}` : 'Join Waiting List'}</h3>
            <p className="modal-subtitle">Identify yourself to continue</p>


            {identifiedUser ? (
              /* State 4: Confirmed user */
              <>
                <div style={{ textAlign: 'center', padding: '16px 0', color: '#fff' }}>
                  <div style={{ fontSize: '48px', marginBottom: '8px' }}>👤</div>
                  <div style={{ fontSize: '20px', fontWeight: '700' }}>
                    {identifiedUser.firstName || identifiedUser.first_name} {identifiedUser.lastName || identifiedUser.last_name}
                  </div>
                </div>

                {userError && <div className="kiosk-modal-error">{userError}</div>}

                <div className="kiosk-modal-actions">
                  <button className="kiosk-btn-secondary" onClick={() => { setIdentifiedUser(null); setUserError(''); setShowCreatePrompt(false); setShowPhonePrompt(false); }}>
                    Not Me
                  </button>
                  <button
                    className="kiosk-btn-primary"
                    onClick={pendingSeat ? handleConfirmSeat : handleConfirmWaitingList}
                    disabled={lookingUp}
                  >
                    {lookingUp ? 'Reserving...' : pendingSeat ? 'Confirm Seat' : 'Join Waiting List'}
                  </button>
                </div>
              </>
            ) : showPhonePrompt ? (
              /* State 3: Phone number & password prompt for new profile */
              <>
                <div style={{ textAlign: 'center', padding: '8px 0 16px', color: '#fff' }}>
                  <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                    Create Profile for {firstName.trim()} {lastName.trim()}
                  </div>
                  <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>
                    Phone number is required for your account
                  </div>
                </div>

                <div className="kiosk-form-group">
                  <label>
                    Phone Number <span style={{ color: '#d4a843' }}>*</span>
                    <span className="kiosk-info-tooltip" title="Required for login and in-game alerts">
                      <span className="kiosk-info-icon">i</span>
                    </span>
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={e => setPhoneNumber(e.target.value)}
                    placeholder="(303) 555-1234"
                    autoFocus
                  />
                </div>

                <div className="kiosk-form-group">
                  <label>
                    Password
                    <span className="kiosk-info-tooltip" title="Passwords allow VIP members to switch seats with non-VIP members, or any open seats.">
                      <span className="kiosk-info-icon">i</span>
                    </span>
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="At least 4 characters (default: 1234)"
                    onKeyDown={e => e.key === 'Enter' && handleCreateProfile()}
                  />
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
                    Passwords allow VIP members to switch seats with non-VIP members, or any open seats.
                  </div>
                </div>

                {userStatusMsg && <div className="kiosk-modal-status">{userStatusMsg}</div>}
                {userError && <div className="kiosk-modal-error">{userError}</div>}

                <div className="kiosk-modal-actions">
                  <button className="kiosk-btn-secondary" onClick={() => { setShowPhonePrompt(false); setUserError(''); }}>
                    Go Back
                  </button>
                  <button className="kiosk-btn-primary" onClick={handleCreateProfile} disabled={lookingUp}>
                    {lookingUp ? (userStatusMsg || 'Creating...') : 'Create Profile'}
                  </button>
                </div>
              </>
            ) : showCreatePrompt ? (
              /* State 2: Create profile confirmation */
              <>
                <div style={{ textAlign: 'center', padding: '20px 0', color: '#fff' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔍</div>
                  <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                    No profile found for {firstName.trim()} {lastName.trim()}
                  </div>
                  <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}>
                    Would you like to create one?
                  </div>
                </div>

                <div className="kiosk-modal-actions">
                  <button className="kiosk-btn-secondary" onClick={() => { setShowCreatePrompt(false); setUserError(''); }}>
                    Go Back
                  </button>
                  <button className="kiosk-btn-primary" onClick={() => { setShowCreatePrompt(false); setShowPhonePrompt(true); }}>
                    Yes, Create Profile
                  </button>
                </div>
              </>
            ) : (
              /* State 1: Lookup form */
              <>
                <div className="kiosk-form-group">
                  <label>First Name *</label>
                  <input
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    placeholder="Enter first name"
                    autoFocus
                  />
                </div>
                <div className="kiosk-form-group">
                  <label>Last Name *</label>
                  <input
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    placeholder="Enter last name"
                    onKeyDown={e => e.key === 'Enter' && handleIdentifyUser()}
                  />
                </div>

                {userStatusMsg && <div className="kiosk-modal-status">{userStatusMsg}</div>}
                {userError && <div className="kiosk-modal-error">{userError}</div>}

                <div className="kiosk-modal-actions">
                  <button className="kiosk-btn-secondary" onClick={() => setShowUserModal(false)}>
                    Cancel
                  </button>
                  <button className="kiosk-btn-primary" onClick={handleIdentifyUser} disabled={lookingUp}>
                    {lookingUp ? (userStatusMsg || 'Looking up...') : 'Find Me'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* VIP Swap — Password Modal */}
      {showVipSwapModal && vipSwapStep === 'password' && (
        <div className="kiosk-user-modal-overlay" onClick={() => setShowVipSwapModal(false)}>
          <div className="kiosk-user-modal" onClick={e => e.stopPropagation()}>
            <h3>VIP Seat Swap</h3>
            <p className="modal-subtitle">
              {vipUser?.playerName}, enter your password to choose a new seat
            </p>

            <div className="kiosk-form-group">
              <label>Password</label>
              <input
                type="password"
                value={vipPassword}
                onChange={e => setVipPassword(e.target.value)}
                placeholder="Enter your VIP password"
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleVipPasswordSubmit()}
              />
            </div>

            {vipSwapError && <div className="kiosk-modal-error">{vipSwapError}</div>}

            <div className="kiosk-modal-actions">
              <button className="kiosk-btn-secondary" onClick={() => setShowVipSwapModal(false)}>
                Cancel
              </button>
              <button className="kiosk-btn-primary" onClick={handleVipPasswordSubmit} disabled={vipSwapLoading}>
                {vipSwapLoading ? 'Verifying...' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VIP Swap — Select Seat Banner */}
      {showVipSwapModal && vipSwapStep === 'selectSeat' && (
        <div className="kiosk-vip-select-banner">
          <span className="vip-select-text">
            <img src={vipIcon} alt="VIP" className="vip-banner-icon" />
            {vipUser?.playerName} — Tap a seat to move to
          </span>
          {vipSwapError && <span className="vip-select-error">{vipSwapError}</span>}
          <button
            className="vip-select-cancel"
            onClick={() => { setShowVipSwapModal(false); setVipSwapError(''); }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* VIP Swap — Confirm Modal */}
      {showVipSwapModal && vipSwapStep === 'confirm' && (
        <div className="kiosk-user-modal-overlay" onClick={() => setVipSwapStep('selectSeat')}>
          <div className="kiosk-user-modal" onClick={e => e.stopPropagation()}>
            <h3>Confirm Seat Swap</h3>
            <div style={{ textAlign: 'center', padding: '16px 0', color: '#fff' }}>
              {vipSwapTarget?.occupant ? (
                <>
                  <p style={{ margin: '0 0 12px', fontSize: '15px', color: 'rgba(255,255,255,0.8)' }}>
                    <strong>{vipUser?.playerName}</strong> will move to <strong>Seat {vipSwapTarget.seatNumber}</strong>
                  </p>
                  <p style={{ margin: 0, fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}>
                    {vipSwapTarget.occupant.playerName} will be moved to your current seat (Seat {vipUser?.seatNumber})
                  </p>
                </>
              ) : (
                <p style={{ margin: 0, fontSize: '15px', color: 'rgba(255,255,255,0.8)' }}>
                  Move <strong>{vipUser?.playerName}</strong> to empty <strong>Seat {vipSwapTarget?.seatNumber}</strong>?
                </p>
              )}
            </div>

            {vipSwapError && <div className="kiosk-modal-error">{vipSwapError}</div>}

            <div className="kiosk-modal-actions">
              <button className="kiosk-btn-secondary" onClick={() => setVipSwapStep('selectSeat')}>
                Back
              </button>
              <button className="kiosk-btn-primary" onClick={handleVipSwapConfirm} disabled={vipSwapLoading}>
                {vipSwapLoading ? 'Swapping...' : 'Confirm Swap'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Login Modal */}
      {showAdminLogin && (
        <div className="kiosk-user-modal-overlay" onClick={() => setShowAdminLogin(false)}>
          <div className="kiosk-user-modal" onClick={e => e.stopPropagation()}>
            <h3>Admin Login</h3>
            <p className="modal-subtitle">Enter admin credentials to access the dashboard</p>

            <div className="kiosk-form-group">
              <label>Username</label>
              <input
                value={adminUsername}
                onChange={e => setAdminUsername(e.target.value)}
                placeholder="Enter admin username"
                autoFocus
                onKeyDown={e => e.key === 'Enter' && adminPassword && handleAdminLoginSubmit()}
              />
            </div>
            <div className="kiosk-form-group">
              <label>Password</label>
              <input
                type="password"
                value={adminPassword}
                onChange={e => setAdminPassword(e.target.value)}
                placeholder="Enter password"
                onKeyDown={e => e.key === 'Enter' && handleAdminLoginSubmit()}
              />
            </div>

            {adminError && <div className="kiosk-modal-error">{adminError}</div>}

            <div className="kiosk-modal-actions">
              <button className="kiosk-btn-secondary" onClick={() => setShowAdminLogin(false)}>
                Cancel
              </button>
              <button className="kiosk-btn-primary" onClick={handleAdminLoginSubmit} disabled={adminLoggingIn}>
                {adminLoggingIn ? 'Signing in...' : 'Sign In'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`kiosk-toast ${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

export default Kiosk;
